import { CheckIcon, validateForm } from '../GroupCompanyInfo'
import React from 'react'
import MenuIcon from '@mui/icons-material/Menu'

import { DeleteButton } from '../../../common/CustomButton'

import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core'

import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'

import CustomTooltip from '../../../../_components/CustomTooltip'

import { CSS } from '@dnd-kit/utilities'
import { AutoSizer, Column, Table } from 'react-virtualized'
import { GroupCompanyInfoModal } from './GroupCompanyInfoModal'
import { formatDate as _formatDate } from 'date-fns'
import { useModifyClassList as modifyClassList } from '../../../../common/utils/CSSUtils'
import { useModal, useModalExtra } from '../../../../_components/Modal'
import { ClientError } from '../../../../common/utils/Exception'

const formatDateYmd = (date) => {
  return _formatDate(date, 'yyyy年MM月')
}

/**
 * @param {Object} props
 * @param {GroupCompanyInfo[]} props.groupCompanyList
 * @param {React.dispatch} props.dispatch
 * @param {Record<string, string[]>} props.inputError
 * @returns {React.ReactNode}
 */
export const GroupCompanyTable = (props) => {
  const { groupCompanyList, dispatch, inputError = {} } = props
  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()

  const headers = groupCompanyHeaders

  const ids = groupCompanyList.map((e) => e.key)

  /**
   * @function updateGroupCompanyInfo 一覧に登録
   * @param {TableData} formData
   */
  async function editGroupCompanyInfo(formData, index) {
    const [isError, errors] = validateForm(formData, groupCompanyList, index)
    if (isError) throw new ClientError(errors)
    dispatch('EDIT_GROUP_COMPANY_INFO_LIST', formData)
  }

  const headerRenderer = ({ columnIndex }) => {
    return <>{headers[columnIndex].label}</>
  }

  // rowGetter関数
  const _rowGetter = ({ index }) => {
    return groupCompanyList[index] || []
  }

  const rowRenderer = ({ rowData, style, colWidths, index }) => {
    const _openCompanyEditModal = () =>
      openCompanyEditModal({
        edit: (data) => editGroupCompanyInfo(data, index),
        groupCompanyInfo: rowData
      })
    return (
      <GroupCompanyTableRowRenderer
        dispatch={dispatch}
        key={rowData.key}
        id={rowData.key}
        rowData={rowData}
        style={style}
        colWidths={colWidths}
        openCompanyEditModal={_openCompanyEditModal}
      />
    )
  }

  /**
   * @function openCompanyEditModal 会社追加処理
   * @typedef {Object} params
   * @property {function} edit
   * @property {GroupCompanyInfo} groupCompanyInfo
   * @param {params} param
   */
  async function openCompanyEditModal({ edit, groupCompanyInfo }) {
    const key = Math.random()

    let formData = null
    let setErrors = null

    const { promisify } = modifyClassList('.modal-window', ['w-[900px]'])

    const handleFormChange = (data, actions) => {
      const { setErrors: _setErrors } = actions
      formData = data
      setErrors = _setErrors
    }

    await promisify((res) => {
      showModal({
        title: 'グループ会社設定',
        onClose: res,
        isWide: false,
        body: (
          <GroupCompanyInfoModal
            editMode={groupCompanyInfo.isRegistered}
            defaultValues={groupCompanyInfo}
            key={key}
            onChange={handleFormChange}
          />
        ),
        button: [
          {
            name: '設定',
            className: 'footer-btn button_base',
            close: false,
            click: async () => {
              try {
                await edit(formData)
                setErrors({})
                _closeUpModal()
              } catch (error) {
                console.log(error)
                if (setErrors !== null) setErrors(error.data)
              }
            }
          }
        ]
      })
    })
  }

  /**
   * レコードの順序を変更する
   *
   * @param {import('@dnd-kit/core').DragEndEvent} e
   */
  const handleDragEnd = (e) => {
    const { active, over } = e
    dispatch('UPDATE_DISPLAY_ORDER', { active, over })
  }

  const calculateWidth = (headers, maxWidth) => {
    const tableContainer = document.querySelector('#group_company_info_table .ReactVirtualized__Grid')
    let tableScrollBarWidth = 0

    if (tableContainer) {
      tableScrollBarWidth = tableContainer.offsetWidth - tableContainer.clientWidth
    }

    const widths = headers.map(({ width }) => {
      if (typeof width === 'number') return width

      if (typeof width === 'string') {
        //％の場合
        if (width.endsWith('%')) return (Number(width.replace('%', '')) / 100) * maxWidth
        if (width === 'auto') return 'auto'
      }

      return 0
    })

    //  幅の合計
    const sumOfWidths = widths.filter((e) => typeof e === 'number').reduce((t, c) => t + c, 0)
    // マックス幅から合計を引く
    const autoSpace = maxWidth - sumOfWidths
    // autoのインデックスを算出
    const index = widths.map((e, i) => (e === 'auto' ? i : null)).filter((x) => !!x)
    //　残のスプースを均等に分けます
    const dividedSpace = autoSpace / (index.length || 1)
    // autoを置き換える
    index.forEach((element) => {
      widths[element] = dividedSpace - tableScrollBarWidth
    })

    return widths
  }

  const rowHeight = 50
  const groupCompanyCount = groupCompanyList.length
  const maxContainerHeight = groupCompanyCount > 500 ? '100vh' : rowHeight * (groupCompanyCount + 1) + 'px'

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className={`setting-table is-segment min-h-[${maxContainerHeight}]`}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <AutoSizer>
            {({ width, height }) => {
              const headerWidth = calculateWidth(headers, width)
              return (
                <Table
                  id={'group_company_info_table'}
                  width={width}
                  height={height}
                  headerHeight={40}
                  rowHeight={rowHeight}
                  rowCount={groupCompanyCount}
                  rowGetter={_rowGetter}
                  rowRenderer={(e) => rowRenderer({ ...e, colWidths: headerWidth })}
                >
                  {headers.map((_, cidx) => {
                    const lastIndex = cidx !== groupCompanyList.length - 1
                    const firstIndex = cidx === 0

                    return (
                      <Column
                        headerClassName={
                          ` border-b border-[#d5d6e5] bg-[#e0e5f2] pt-[6.5px] px-4 pb-[7.5px] font-bold text-sm ` +
                          ` text-[#7476a6] ` +
                          ` ${lastIndex && 'border-l border-[#7476a633]'} ` +
                          ` ${firstIndex && 'justify-center'} `
                        }
                        width={headerWidth[cidx] || 100}
                        key={cidx}
                        dataKey={`col-${cidx}`}
                        headerRenderer={() => headerRenderer({ columnIndex: cidx })}
                      />
                    )
                  })}
                </Table>
              )
            }}
          </AutoSizer>
        </SortableContext>
        {/* エラーメッセージ表示 */}
        {Object.keys(inputError)
          .filter((key) => key !== 'errorMessage')
          .map((key, idx) => (
            <span key={key} className="red--text">
              {idx !== 0 && <br />}
              {inputError[key].datePeriod}
            </span>
          ))}
      </div>
    </DndContext>
  )
}

const GroupCompanyTableRowRenderer = ({ rowData, id, openCompanyEditModal, style: _style, dispatch, colWidths }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })

  const { isDeleted, isError } = rowData

  const style = {
    ..._style,
    transform: CSS.Transform.toString(transform),
    transition
  }

  /**
   * @function handleDelete 削除と復元処理
   * @param {string} key
   * @param {boolean} restore
   */
  const handleDelete = (key, restore = false) => {
    dispatch('DELETE_GROUP_COMPANY_INFO_LIST', { key, restore })
  }

  const onDelete = () => handleDelete(rowData.key)
  const onRestore = () => handleDelete(rowData.key, true)

  const actions = { onDelete, onRestore, openCompanyEditModal }

  return (
    <div
      data-testid={`row_${rowData?.key}`}
      ref={setNodeRef}
      className={`${isDeleted && 'bg-gray-300'} ${isError && 'outline outline-[0.5px] outline-red-500'} flex`}
      style={style}
      {...attributes}
    >
      {groupCompanyHeaders.map((header, index) => {
        return (
          <div
            data-testid={`test_${header.key}`}
            style={{ width: colWidths[index] }}
            key={index}
            className={
              `font-normal px-4 py-3 border-b border-[#e9ebf1] bg-[#3150a508] text-base ` +
              ` ${index !== 0 && 'border-l border-l-[#7476a633]'}  `
            }
          >
            {header.render(rowData, actions, listeners)}
          </div>
        )
      })}
    </div>
  )
}

const groupCompanyHeaders = [
  {
    label: '↑↓',
    key: 'dragControl',
    width: 100,
    render(_, __, _listeners = {}) {
      return (
        <div className="cursor-pointer order-icon" {..._listeners}>
          <MenuIcon />
        </div>
      )
    }
  },

  {
    label: '法人番号',
    key: 'corporationNumber',
    width: '15%',
    render(data) {
      const { isDeleted, corporationNumber } = data
      return <div className={` whitespace-nowrap ${isDeleted && 'line-through'}`}> {corporationNumber} </div>
    }
  },
  {
    label: '会社名',
    key: 'compName',
    width: 'auto',
    render(data, actions) {
      const { compName, isDeleted, isChrofyEnabled } = data

      const isLinkDisabled = isDeleted || isChrofyEnabled
      const { openCompanyEditModal } = actions

      return (
        <CustomTooltip title={compName}>
          {(ref) => {
            return (
              <div className={`truncate block ${isDeleted && 'line-through'}`} ref={ref}>
                {!isLinkDisabled && (
                  <a
                    target="_blank"
                    role="button"
                    className={'text-blue-500  cursor-pointer underline-offset-4'}
                    onClick={openCompanyEditModal}
                  >
                    {compName}
                  </a>
                )}
                {isLinkDisabled && compName}
                <span className={'inline-block ml-3 '}>
                  {isChrofyEnabled && <CheckIcon className={`${isDeleted && 'text-gray-400'}`} />}
                </span>
              </div>
            )
          }}
        </CustomTooltip>
      )
    }
  },
  {
    label: '会社種類',
    key: 'compType',
    width: 150,
    render(rowData) {
      const { isDeleted, compType } = rowData
      return <div className={`${isDeleted && 'line-through'}`}>{compType}</div>
    }
  },
  {
    label: '有効開始月',
    key: 'validStartDate',
    width: 200,
    render(rowData) {
      const { isDeleted, validStartDate } = rowData

      const _validStartDate = validStartDate ? formatDateYmd(validStartDate) : null

      return <div className={`${isDeleted && 'line-through'}`}>{_validStartDate ?? '-'}</div>
    }
  },
  {
    label: '有効終了月',
    key: 'validEndDate',
    width: 200,
    render(rowData) {
      const { isDeleted, validEndDate } = rowData
      const _validEndDate = validEndDate ? formatDateYmd(validEndDate) : null

      return <div className={`${isDeleted && 'line-through'}`}>{_validEndDate ?? '-'}</div>
    }
  },
  {
    label: '',
    key: 'actions',
    width: 100,
    render(rowData, actions) {
      const { isDeleted, isChrofyEnabled, memo } = rowData

      const { onDelete, onRestore } = actions

      return (
        <div className={''}>
          <div className="flex gap-x-2">
            <div className="flex-1 inline-flex items-center justify-center">
              {!isChrofyEnabled && (
                <CustomTooltip title={<div className="whitespace-pre-wrap">{memo}</div>} forceShow={!isDeleted && memo}>
                  <span
                    data-testid={`memo_${memo ? 'enabled' : 'disabled'}`}
                    className={`icon-memo text-gray-500 ${memo && !isDeleted && '!text-red-500'}`}
                  ></span>
                </CustomTooltip>
              )}
            </div>
            <div className="flex-1  inline-flex items-center justify-center">
              {!isChrofyEnabled && <DeleteButton isRestore={isDeleted} onDelete={onDelete} onRestore={onRestore} />}
            </div>
          </div>
        </div>
      )
    }
  }
]
